import { cva, type VariantProps } from "class-variance-authority";
import clsx from "clsx";
import type { RefAttributes } from "react";
import { StatusIndicator, type StatusIndicatorProps } from "../status-indicator";
import type { ChildrenProps } from "../types";

/** Props Interface */
export interface TagProps
	extends ChildrenProps,
		Omit<StatusIndicatorProps, "size">,
		VariantProps<typeof tag> {
	onClick?: () => void;
	withStatusIndicator?: boolean;
	ref?: RefAttributes<HTMLDivElement>["ref"];
}

export function Tag({
	color = "gray",
	withStatusIndicator = true,
	withPing,
	children,
	onClick,
	className,
	variant = "default",
	transform = "default",
	fontStyle = "default",
	direction,
	size,
	ref,
}: TagProps) {
	/** Computed */
	const isClickable = onClick !== undefined;
	const classes = tag({ variant, transform, fontStyle, direction, size });

	/** Render */
	return (
		<div
			className={clsx(
				"inline-flex flex-row items-center gap-x-2 rounded-base",
				"w-max",
				"shrink-0",
				"text-color",
				isClickable && "cursor-pointer",
				classes,
				className,
			)}
			ref={ref}
			onClick={onClick}
			onKeyUp={onClick}
		>
			{withStatusIndicator && (
				<StatusIndicator
					color={color}
					withPing={withPing}
				/>
			)}
			{children && <p>{children}</p>}
		</div>
	);
}

const tag = cva([], {
	variants: {
		variant: {
			default: "px-2.5 py-1 card-bg border-[0.5px] border-color",
			plain: "",
		},
		size: {
			xs: "text-xs",
			sm: "text-sm",
			md: "text-base",
			lg: "text-lg",
		},
		transform: {
			capitalize: "capitalize",
			uppercase: "uppercase",
			default: "",
		},
		fontStyle: {
			mono: "font-mono",
			default: "",
		},
		direction: {
			row: "",
			"row-reverse": ["flex-row-reverse"],
		},
	},
	defaultVariants: {
		variant: "default",
		transform: "default",
		fontStyle: "default",
		size: "sm",
	},
});
